import { useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { useState, useEffect, useCallback } from 'react';
import { apiFailureDetector, ApiFailureState } from '../utils/apiFailureDetection';
import { FallbackDataService, FallbackResult } from '../utils/fallbackDataService';
import { performanceMonitor } from '../utils/performanceMonitor';

export interface UseFallbackQueryOptions<T> extends Omit<UseQueryOptions<T>, 'queryFn' | 'queryKey'> {
  fetcher: () => Promise<T>;
  fallbackValue?: T;
  maxCacheAge?: number;
  priority?: 'high' | 'medium' | 'low';
  showStaleIndicator?: boolean;
  enablePrefetch?: boolean;
  endpoint?: string;
}

export interface UseFallbackQueryResult<T> {
  data: T | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  isFromCache: boolean;
  isStale: boolean;
  cacheAge: number;
  lastUpdated: number;
  source: 'api' | 'cache' | 'fallback';
  isOffline: boolean;
  refetch: () => Promise<void>;
  prefetch: () => Promise<void>;
}

export function useFallbackQuery<T>(
  queryKey: string[],
  options: UseFallbackQueryOptions<T>
): UseFallbackQueryResult<T> {
  const queryClient = useQueryClient();
  const [fallbackService] = useState(() => new FallbackDataService(queryClient));
  const [apiState, setApiState] = useState<ApiFailureState>(() => apiFailureDetector.getFailureState());
  const [fallbackResult, setFallbackResult] = useState<FallbackResult<T> | null>(null);

  const {
    fetcher,
    fallbackValue,
    maxCacheAge = 30 * 60 * 1000,
    priority = 'medium',
    showStaleIndicator = true,
    enablePrefetch = false,
    endpoint,
    ...reactQueryOptions
  } = options;

  // Listen to API failure state changes
  useEffect(() => {
    const unsubscribe = apiFailureDetector.addListener(setApiState);
    return unsubscribe;
  }, []);

  // Enhanced fetcher with performance monitoring and fallback logic
  const enhancedFetcher = useCallback(async (): Promise<T> => {
    const startTime = Date.now();
    const queryKeyString = queryKey.join(':');

    try {
      const result = await fallbackService.getDataWithFallback(
        queryKey,
        fetcher,
        {
          maxAge: maxCacheAge,
          priority,
          fallbackValue,
          showStaleIndicator,
        }
      );

      setFallbackResult(result);

      // Record performance metrics
      performanceMonitor.recordRequest({
        timestamp: Date.now(),
        queryKey: queryKeyString,
        responseTime: Date.now() - startTime,
        source: result.source,
        success: result.data !== null,
        cacheAge: result.cacheAge,
        endpoint: endpoint || queryKey[0],
      });

      if (result.data === null) {
        throw new Error('No data available from API, cache, or fallback');
      }

      return result.data;
    } catch (error) {
      // Record failed request
      performanceMonitor.recordRequest({
        timestamp: Date.now(),
        queryKey: queryKeyString,
        responseTime: Date.now() - startTime,
        source: 'api',
        success: false,
        endpoint: endpoint || queryKey[0],
      });

      throw error;
    }
  }, [queryKey, fetcher, fallbackService, maxCacheAge, priority, fallbackValue, showStaleIndicator, endpoint]);

  // Use React Query with enhanced fetcher
  const query = useQuery({
    queryKey,
    queryFn: enhancedFetcher,
    staleTime: maxCacheAge,
    gcTime: maxCacheAge * 2,
    retry: (failureCount, error) => {
      const shouldUseFallback = apiFailureDetector.shouldUseFallback(endpoint);
      return !shouldUseFallback && failureCount < 3;
    },
    ...reactQueryOptions,
  });

  // Prefetch function
  const prefetch = useCallback(async () => {
    if (!enablePrefetch) return;

    try {
      await fallbackService.prefetchData(queryKey, fetcher, {
        priority,
        maxAge: maxCacheAge,
      });
    } catch (error) {
      console.warn('Prefetch failed:', error);
    }
  }, [enablePrefetch, fallbackService, queryKey, fetcher, priority, maxCacheAge]);

  // Enhanced refetch that respects fallback state
  const refetch = useCallback(async () => {
    if (apiState.isOffline) {
      console.info('Skipping refetch - in offline mode');
      return;
    }

    await query.refetch();
  }, [query, apiState.isOffline]);

  // Auto-prefetch on mount if enabled
  useEffect(() => {
    if (enablePrefetch && !query.data) {
      prefetch();
    }
  }, [enablePrefetch, prefetch, query.data]);

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isFromCache: fallbackResult?.isFromCache || false,
    isStale: fallbackResult?.isStale || false,
    cacheAge: fallbackResult?.cacheAge || 0,
    lastUpdated: fallbackResult?.lastUpdated || 0,
    source: fallbackResult?.source || 'api',
    isOffline: apiState.isOffline,
    refetch,
    prefetch,
  };
}

// Hook for cache warming
export function useCacheWarming() {
  const queryClient = useQueryClient();
  const [fallbackService] = useState(() => new FallbackDataService(queryClient));
  const [isWarming, setIsWarming] = useState(false);

  const warmCache = useCallback(async (criticalQueries: Array<{
    queryKey: string[];
    fetcher: () => Promise<any>;
    priority?: 'high' | 'medium' | 'low';
  }>) => {
    setIsWarming(true);
    try {
      await fallbackService.warmCache(criticalQueries.map(q => ({
        ...q,
        options: { priority: q.priority || 'high' },
      })));
    } finally {
      setIsWarming(false);
    }
  }, [fallbackService]);

  return { warmCache, isWarming };
}

// Hook for performance monitoring
export function usePerformanceMetrics(timeWindow?: number) {
  const [metrics, setMetrics] = useState(() => performanceMonitor.getMetrics(timeWindow));

  useEffect(() => {
    const interval = setInterval(() => {
      setMetrics(performanceMonitor.getMetrics(timeWindow));
    }, 5000); // Update every 5 seconds

    return () => clearInterval(interval);
  }, [timeWindow]);

  const getEfficiencyReport = useCallback(() => {
    return performanceMonitor.getCacheEfficiencyReport();
  }, []);

  const getTopSlowQueries = useCallback((limit?: number) => {
    return performanceMonitor.getTopSlowQueries(limit, timeWindow);
  }, [timeWindow]);

  const exportMetrics = useCallback(() => {
    return performanceMonitor.exportMetrics();
  }, []);

  const clearMetrics = useCallback(() => {
    performanceMonitor.clearMetrics();
    setMetrics(performanceMonitor.getMetrics(timeWindow));
  }, [timeWindow]);

  return {
    metrics,
    getEfficiencyReport,
    getTopSlowQueries,
    exportMetrics,
    clearMetrics,
  };
}

// Hook for API failure state
export function useApiFailureState() {
  const [state, setState] = useState<ApiFailureState>(() => apiFailureDetector.getFailureState());

  useEffect(() => {
    const unsubscribe = apiFailureDetector.addListener(setState);
    return unsubscribe;
  }, []);

  return state;
}